<template>
  <div class="r-date-picker">
    <label v-if="props.label" class="dp-label">
      {{ props.label }}
    </label>
    <!-- Custom wrapper for the datepicker to ensure icon visibility -->
    <div class="date-picker-container">
      <datepicker
        ref="datePicker"
        v-model="range"
        range
        :multi-calendars="props.multiCalendars"
        prevent-min-max-navigation
        :placeholder="props.placeholder"
        :enable-time-picker="false"
        format="MM/dd/yyyy"
        :start-date="props.customStartDate || startDate"
        :min-date="props.allowPastDates ? props.minDate : currentDate"
        :max-date="props.allowFutureDates ? props.customMaxDate : maxDate"
        :config="config"
        teleport
        :class="{ 'hidden-clear-icon': true }"
        @open="isDatePickerOpen = true"
        @closed="isDatePickerOpen = false"
      >
        <template #action-row="{ selectDate }">
          <div class="action-row row full-width justify-between">
            <div class="preset-dropdown col-6">
              <div class="clickable" @click="presentDateRangeDropdownOpen = !presentDateRangeDropdownOpen">
                Date Range
                <span class="material-icons-outlined" style="font-size: 2em;">
                  arrow_drop_down
                </span>
              </div>
            </div>
            <div class="select-button col-auto clickable text-center" @click="close">
              Cancel
            </div>
            <div class="select-button col-auto clickable text-center q-px-md" data-test="selectDateRange" @click="selectDate">
              OK
            </div>
            <div v-if="presentDateRangeDropdownOpen" class="col-12">
              <v-row class="pa-1">
                <v-col cols="6">
                  <v-list>
                    <v-list-item clickable @click="quickRangeSelect(7)">
                      <v-list-item-title>Last 7 Days</v-list-item-title>
                    </v-list-item>
                    <v-list-item clickable @click="quickRangeSelect(30)">
                      <v-list-item-title>Last 30 Days</v-list-item-title>
                    </v-list-item>
                    <v-list-item clickable @click="quickRangeSelect(90)">
                      <v-list-item-title>Last 90 Days</v-list-item-title>
                    </v-list-item>
                    <v-list-item clickable @click="quickRangeSelect(180)">
                      <v-list-item-title>Last 180 Days</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-col>
                <v-col cols="6">
                  <v-list>
                    <v-list-item clickable @click="quickRangeSelect('last_quarter')">
                      <v-list-item-title>Last Quarter</v-list-item-title>
                    </v-list-item>
                    <v-list-item clickable @click="quickRangeSelect('quarter_to_date')">
                      <v-list-item-title>Quarter to Date</v-list-item-title>
                    </v-list-item>
                    <v-list-item clickable @click="quickRangeSelect('last_year')">
                      <v-list-item-title>Last Year</v-list-item-title>
                    </v-list-item>
                    <v-list-item clickable @click="quickRangeSelect('year_to_date')">
                      <v-list-item-title>Year to Date</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-col>
              </v-row>
            </div>
          </div>
        </template>
      </datepicker>
      <!-- Manually add an icon that sits on top of everything -->
      <button 
        v-if="!range"
        class="custom-dropdown-icon" 
        type="button"
        @click="toggleDatePicker"
      >
        <RelIcon :name="isDatePickerOpen ? 'arrow_drop_up' : 'arrow_drop_down'" type="symbol" />
      </button>
    </div>
  </div>
</template>

<script setup>
import { defineEmits, defineProps, computed, ref } from 'vue'
import { object, array } from 'vue-types'
import dayjs from 'dayjs'
import quarterOfYear from 'dayjs/plugin/quarterOfYear'

import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import { RelIcon } from '@/components-rel'
dayjs.extend(quarterOfYear)

const props = defineProps({
  dateRange: array(),
  minDate: object(),
  multiCalendars: {
    type: Boolean,
    default: true,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: 'All Time',
  },
  allowPastDates: {
    type: Boolean,
    default: false,
  },
  allowFutureDates: {
    type: Boolean,
    default: false,
  },
  customStartDate: {
    type: [String, Object, Date],
    default: null,
  },
  customMaxDate: {
    type: [String, Object, Date],
    default: null,
  },
})

const emit = defineEmits(['update:dateRange'])

const config = {
  shadowDom: true,
  allowPreventDefault: true,
  autoApply: false,
  teleport: true,
  teleportTo: 'body',
  teleportOptions: {
    appendTo: () => document.body,
    to: 'body',
    disabled: false
  }
}

const presentDateRangeDropdownOpen = ref(false)
const isDatePickerOpen = ref(false)
const startDate = computed(() => dayjs(new Date).startOf('month').subtract(1, 'month').format('MM/DD/YYYY'))
const currentDate = computed(() => new Date())
const maxDate = computed(() => {
  const oneYearFromNow = new Date()
  oneYearFromNow.setFullYear(oneYearFromNow.getFullYear() + 1)
  return oneYearFromNow
})
const datePicker = ref()

const range = computed({
  get() {
    return props.dateRange
  },
  set(value) {
    emit('update:dateRange', value)
  }
})

function close() {
  datePicker.value.closeMenu()
}

function toggleDatePicker() {
  if (isDatePickerOpen.value) {
    close()
  } else {
    datePicker.value.openMenu()
  }
}

function quickRangeSelect(range) {
  let from, to
  const minDate = dayjs(props.minDate)

  if (typeof range === 'number') {
    from = dayjs(new Date).subtract(range - 1, 'day')
    to = dayjs(new Date())
  } else {
    switch(range) {
    case 'last_quarter':
      from = dayjs(new Date).subtract(1, 'quarter').startOf('quarter')
      to = dayjs(new Date).subtract(1, 'quarter').endOf('quarter')
      break
    case 'quarter_to_date':
      from = dayjs(new Date).startOf('quarter')
      to = dayjs(new Date())
      break
    case 'last_year':
      // We only have data as far back as July 2022, so we need to make sure we don't go back further than that
      if (props.minDate && minDate.isAfter(dayjs(new Date).subtract(1, 'year').startOf('year'))) {
        from = minDate
      } else {
        from = dayjs(new Date).subtract(1, 'year').startOf('year')
      }
      to = dayjs(new Date).subtract(1, 'year').endOf('year')
      break
    case 'year_to_date':
      from = dayjs(new Date).startOf('year')
      to = dayjs(new Date())
      break
    }
  }

  emit('update:dateRange', [from, to])
  close()
}
</script>

<style>

.dp__input {
padding-top: 7px !important;
border-color: darkgrey;
--vf-color-placeholder: #5B5B5B;
font-size: 16px;
}

.dp__input_focus {
  padding-top: 7px !important;
}

.date-picker-container {
  position: relative;
  width: 100%;
}

.custom-dropdown-icon {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 9999;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: auto;
}

/* Only hide clear icon when we're showing our custom dropdown */
.hidden-clear-icon:has(.custom-dropdown-icon) :deep(.dp__clear_icon) {
  display: none !important;
}

.r-date-picker :deep(.dp__input) {
  padding-right: 40px !important;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.dp__theme_light {
  --dp-primary-color: #001749;
  --dp-hover-color: #E6ECF7;
  --dp-text-color: #5B5B5B;
  --dp-icon-color: rgba(0, 0, 0, 0.54);
}
.dp__month_year_wrap {
  justify-content: center;
}

/* Prevent datepicker from being cut off */
.dp__outer_menu_wrap {
  z-index: 9999 !important;
  position: fixed !important;
}

.dp__menu {
  transform: none !important; /* Allow natural positioning */
  overflow: visible !important;
  max-height: none !important;
}

.dp__instance_calendar {
  width: auto !important;
  overflow: visible !important;
}

.dp__month_year_select {
  width: 40%;
}
.dp__range_start, .dp__range_end {
  border-radius: 100%;
}
.dp__range_start::after, .dp__range_end::after {
  content: "";
  position: absolute;
  background: #E6ECF7;
  top: -1px;
  bottom: -1px;
  left: -1px;
  right: -1px;
  z-index: -1;
}
.dp__range_start::after {
  border-top-left-radius: 100%;
  border-bottom-left-radius: 100%;
}
.dp__range_end::after {
  border-top-right-radius: 100%;
  border-bottom-right-radius: 100%;
}
.dp__overlay_cell_active {
  --dp-primary-color: #E6ECF7;
  color: initial;
}
</style>

<style lang="scss" scoped>
:deep(.dp__main) {
  height: 100%;
}
:deep(.arrow-drop) {
  transition: all 300ms;
  padding: 10px 4px;

  &.arrow-drop-up {
    transform: rotate(180deg);
  }
}

@media (min-width: $breakpoint-lg-min) {
.r-date-picker {
    width: 262px;
  }
}

:deep(.v-list-item--density-default:not(.v-list-item--nav).v-list-item--one-line) {
  padding: 0;
}
</style>